import React, { useState } from "react";
import { X, Send, CheckCircle2 } from "lucide-react";
import { courses, brand } from "../mock";
import { toast } from "../hooks/use-toast";

export default function LeadModal({ open, onClose, preselect }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    course: preselect || "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  React.useEffect(() => {
    if (preselect) setForm((f) => ({ ...f, course: preselect }));
  }, [preselect]);

  if (!open) return null;

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast({ title: "Please share your name and phone", description: "We need these to reach you back." });
      return;
    }
    setSubmitting(true);

    const lines = [
      "*New Demo Booking - CYA Youth Academy*",
      "",
      `*Name:* ${form.name}`,
      `*Phone:* ${form.phone}`,
    ];
    if (form.email) lines.push(`*Email:* ${form.email}`);
    if (form.course) lines.push(`*Course:* ${form.course}`);
    if (form.message) lines.push(`*Message:* ${form.message}`);
    lines.push("", "_Sent via cyayouthacademy website_");

    const text = encodeURIComponent(lines.join("\n"));
    const waUrl = `https://wa.me/918249622167?text=${text}`;

    try {
      const list = JSON.parse(localStorage.getItem("cya_leads") || "[]");
      list.push({ ...form, at: new Date().toISOString() });
      localStorage.setItem("cya_leads", JSON.stringify(list));
    } catch (_) {}

    window.open(waUrl, "_blank", "noopener,noreferrer");

    setTimeout(() => {
      setSubmitting(false);
      setDone(true);
      toast({
        title: "Opening WhatsApp…",
        description: "Please tap Send in WhatsApp to complete your booking.",
      });
    }, 600);
  };

  const reset = () => {
    setDone(false);
    setForm({ name: "", phone: "", email: "", course: preselect || "", message: "" });
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-[#0A1740]/60 backdrop-blur-sm" onClick={reset} />
      <div className="relative w-full max-w-lg rounded-3xl border border-[#0A1740]/10 bg-white shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 pt-6">
          <div>
            <div className="text-[#B45309] text-xs font-black tracking-[0.3em] uppercase">Free Demo</div>
            <h3 className="text-2xl font-extrabold brand-navy mt-1">Reserve your seat</h3>
          </div>
          <button onClick={reset} className="text-[#0A1740]/70 hover:brand-navy p-2">
            <X />
          </button>
        </div>

        {done ? (
          <div className="px-6 py-10 text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-[#FEF3C7] flex items-center justify-center text-[#B45309]">
              <CheckCircle2 size={34} />
            </div>
            <h4 className="mt-4 text-xl font-extrabold brand-navy">Almost there, {form.name.split(" ")[0]}!</h4>
            <p className="mt-2 text-[#0A1740]/65 text-sm">
              We've opened WhatsApp with your details. Just tap <span className="text-[#B45309] font-bold">Send</span> and our counsellor will reach out shortly.
            </p>
            <p className="mt-3 text-[#0A1740]/50 text-xs">
              Didn't see WhatsApp open?{' '}
              <a
                href={`https://wa.me/918249622167?text=${encodeURIComponent(`Hi, I'm ${form.name}. I want to book a free demo${form.course ? ` for ${form.course}` : ''}. My number is ${form.phone}.`)}`}
                target="_blank" rel="noreferrer"
                className="text-[#B45309] underline font-semibold"
              >Click here</a> to open it again.
            </p>
            <button onClick={reset} className="mt-6 btn-primary px-5 py-2.5 rounded-full">Close</button>
          </div>
        ) : (
          <form onSubmit={submit} className="px-6 py-5 space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <Input label="Full Name*" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="e.g. Priya Sri" />
              <Input label="Phone*" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} placeholder="10-digit mobile" type="tel" />
            </div>
            <Input label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} placeholder="you@example.com" type="email" />
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#0A1740]/60 mb-1.5">Course Interested</label>
              <select
                value={form.course}
                onChange={(e) => setForm({ ...form, course: e.target.value })}
                className="w-full bg-[#FFF9EF] border border-[#0A1740]/15 rounded-xl px-3.5 py-3 brand-navy text-sm focus:border-[#F59E0B] focus:outline-none"
              >
                <option value="">Select a course</option>
                {courses.map((c) => (
                  <option key={c.id} value={c.title}>{c.title}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#0A1740]/60 mb-1.5">Message</label>
              <textarea
                rows={3}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Any preferred timing or questions?"
                className="w-full bg-[#FFF9EF] border border-[#0A1740]/15 rounded-xl px-3.5 py-3 brand-navy text-sm focus:border-[#F59E0B] focus:outline-none resize-none"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full py-3.5 rounded-xl inline-flex items-center justify-center gap-2 disabled:opacity-70"
            >
              {submitting ? "Opening WhatsApp…" : (<>Send via WhatsApp <Send size={16} /></>)}
            </button>
            <p className="text-[11px] text-[#0A1740]/55 text-center">
              Your details will open in WhatsApp – just tap Send. We respect your privacy.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

function Input({ label, value, onChange, placeholder, type = "text" }) {
  return (
    <div>
      <label className="block text-xs font-bold uppercase tracking-wider text-[#0A1740]/60 mb-1.5">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-[#FFF9EF] border border-[#0A1740]/15 rounded-xl px-3.5 py-3 brand-navy text-sm focus:border-[#F59E0B] focus:outline-none"
      />
    </div>
  );
}
